import { css } from '@emotion/react';
import { ErrorWrapper } from '@web/components/error-wrapper';
import { Loading } from '@web/components/loading';
import { AppearanceNav } from '@web/features/insights/appearance-nav';
import { InsightsCalendar } from '@web/features/insights/insights-calendar';
import { InsightsSpiral } from '@web/features/insights/spiral';
import { pointColors } from '@web/features/insights/spiral-utils';
import {
  getTypeText,
  getTypeTitle,
  TypeNav,
} from '@web/features/insights/type-nav';
import { useInsightsData } from '@web/features/insights/use-insights-data';
import { UserLayout } from '@web/features/layouts/user';
import { theme } from '@web/features/theme';
import { useRouter } from 'next/router';

export default function InsightsPage() {
  const router = useRouter();
  const [state, { clearError, getCustomStyles, setType, setAppearance }] =
    useInsightsData(router);

  const custom = getCustomStyles(state.type);

  switch (state.status) {
    case 'PENDING': {
      return (
        <UserLayout withHomeButton>
          <Loading>Fetching required data</Loading>
        </UserLayout>
      );
    }

    case 'ERROR': {
      return (
        <UserLayout withHomeButton>
          <ErrorWrapper error={state.error} clearError={clearError} />
        </UserLayout>
      );
    }

    default: {
      return (
        <UserLayout
          withHomeButton
          withBlur
          background={`${custom.backgroundColor ?? custom.color} url('/images/${
            custom.url
          }');
            background-size: cover;
            background-position: top 15% center;
            background-repeat: no-repeat;

          `}
        >
          <h1>{getTypeTitle(state.type)} Insights</h1>
          <p>{getTypeText(state.type)}</p>

          <div className="rr-mask" css={styles.content}>
            <AppearanceNav
              appearance={state.appearance}
              setAppearance={setAppearance}
            />

            <TypeNav
              type={state.type}
              setType={setType}
              customStyles={custom}
            />

            {state.appearance !== 'calendar' ? null : (
              <InsightsCalendar key="calendar" {...state} />
            )}

            {state.appearance !== 'spiral' ? null : (
              <InsightsSpiral key="spiral" {...state} />
            )}

            <Legend />
          </div>
        </UserLayout>
      );
    }
  }
}

const Legend = () => (
  <div css={styles.legendWrapper}>
    <div css={styles.legendGradient} />
    <span css={styles.legendLabel}>Overall Low</span>
    <span css={styles.legendLabel}>Overall High</span>
  </div>
);

const styles = {
  content: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-around;
    max-width: ${theme.space.lg};
    margin: ${theme.space[12]} auto 0;
  `,

  legendWrapper: css`
    flex: 1 1 75%;
    width: 75%;
    max-width: ${theme.space['72']};
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  `,

  legendLabel: css`
    max-width: 6ch;
    margin-top: ${theme.space[4]};
    text-align: center;
    font-size: ${theme.fontSizes.sm};
  `,

  legendGradient: css`
    position: relative;
    flex: 1 1 100%;
    height: ${theme.space[4]};
    width: 100%;
    border-top: ${theme.borders['1px']} ${theme.colors.whiteAlpha[900]};
    border-bottom: ${theme.borders['1px']} ${theme.colors.whiteAlpha[900]};
    background: linear-gradient(
      to right,
      ${pointColors
        .slice(0, pointColors.length - 1) /** get rid of 'transparent' */
        .map((color, idx, arr) => `${color} ${(idx / (arr.length - 1)) * 100}%`)
        .join(', ')}
    );

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: -30%;
      height: ${theme.space[8]};
      width: 2px;
      background-color: ${theme.colors.whiteAlpha[900]};
    }

    &:before {
      left: 0;
    }

    &:after {
      right: 0;
    }
  `,
};
